import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import { Box, Grid, Paper, Typography, Popover, SvgIcon, Button, Tooltip, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';

import {
  getNotesInMode,
  getChordDescriptorsInMode,
  getScaleDegrees,
  getDefaultProgression,
  resolveAllNotesIndex,
  formatChordQuality,
  RANDOM_PROGRESSION
} from '../utils/musicTheory';
import {
  getProgressionByName,
  degreeToIndex
} from '../utils/chordProgressions';
import { getCompatibleProgressions } from '../utils/progressionCompatibility';
import { useSynthPlayer } from '../hooks/useSynthPlayer';
import MetronomeSettings from './MetronomeSettings';
import ChordConfigPanel from './ChordConfigPanel';
import CustomToast from './CustomToast';
import formatAccidentals from '../utils/formatAccidentals';
import AccidentalText from './AccidentalText';
import CardOverflowButton from './CardOverflowButton';
import { track } from '../utils/analytics';
import Tip, { coachBlinkSx } from './Tip';

const canHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;

// Contenu des trois cartes de controle (progression, metronome, voicing).
// Le decalage vers le bas degage les 26px que prend CardOverflowButton dans
// l'angle : sans lui, l'icone primaire repasse sous la cible du menu.
const CARD_CONTENT_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  paddingTop: '6px',
  // En paysage la carte tombe a 72x51 : il n'y a plus de place pour degager
  // le menu vers le bas, on degage la colonne de droite a la place.
  'body.compact-mode &': {
    paddingTop: 0,
    paddingRight: '16px'
  }
};

// Simple custom metronome icon rendered with SvgIcon so we do not rely on optional icon packs
const MetronomeIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M12 2 5 20h2.8l-.9 2h10.2l-.9-2H19L12 2zm0 4.1 3.25 8.9H8.75L12 6.1z" />
    <path d="m17.3 8.95-1.08 1.08 2.6 2.6-.9 2.07h1.63l1.2-2.76-3.45-2.99z" />
  </SvgIcon>
);

// Styled components for the note buttons
const NoteButton = styled(Paper, {
  shouldForwardProp: (prop) => !['active', 'playing', 'isroot', 'inmode', 'isnext', 'coachblink'].includes(prop)
})(({ theme, active = false, playing = false, isroot = false, inmode = false, isnext = false, coachblink = false }) => ({
  padding: theme.spacing(0.75),
  textAlign: 'center',
  cursor: 'pointer',
  position: 'relative',
  transition: 'background-color 0.3s ease, color 0.3s ease, opacity 0.3s ease',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '8px',
  transform: 'none', // Removed all transform effects
  backgroundColor: playing
      ? 'rgba(var(--hl-root-rgb), 0.14)' // Dark muted green background for playing chord
      : isnext
        ? 'rgba(var(--accent-rgb), 0.15)' // Light blue background for next chord
        : inmode
          ? 'var(--surface)' // Dark background for normal notes
          : 'rgba(20, 20, 22, 0.5)', // Darker for out-of-scale notes
  color: playing
      ? 'var(--hl-root)' // Modern muted green text for playing chord
      : isnext
        ? 'var(--accent)' // Blue text for next chord
        : active
          ? 'rgba(var(--accent-rgb), 0.7)' // Lighter blue for active
          : inmode
            ? 'rgba(255, 255, 255, 0.8)' // Brighter text for in-scale notes
            : 'rgba(255, 255, 255, 0.3)', // Dimmer text for out-of-scale notes
  opacity: inmode ? 0.9 : 0.5, // Consistent opacity, removed active state change
  boxShadow: playing
    ? 'inset 0 0 0 2px var(--hl-root), 0 0 10px rgba(var(--hl-root-rgb), 0.3)' // Inset border for playing (muted green)
    : isnext
      ? 'inset 0 0 0 2px var(--accent), 0 0 10px rgba(var(--accent-rgb), 0.4)' // Inset border for next
      : 'none',
  border: 'none', // Removed regular border, using inset box-shadow instead
  pointerEvents: inmode ? 'auto' : 'none', // Disable clicks on out-of-scale notes
  ...(coachblink && coachBlinkSx),
  '&:hover': {
    transform: 'none', // Removed hover transform
    boxShadow: playing
      ? 'inset 0 0 0 2px var(--hl-root), 0 0 10px rgba(var(--hl-root-rgb), 0.3)' // Keep inset border on hover (muted green)
      : isnext
        ? 'inset 0 0 0 2px var(--accent), 0 0 10px rgba(var(--accent-rgb), 0.4)'
        : 'none',
    backgroundColor: inmode
      ? 'var(--surface-2)' // Darker for in-scale notes on hover
      : 'rgba(28, 28, 31, 0.2)', // No change for out-of-scale notes
  },
  '&:active': {
    transform: 'none', // Removed active transform
    boxShadow: playing
      ? 'inset 0 0 0 2px var(--hl-root), 0 0 10px rgba(var(--hl-root-rgb), 0.3)' // Keep inset border on active (muted green)
      : isnext
        ? 'inset 0 0 0 2px var(--accent), 0 0 10px rgba(var(--accent-rgb), 0.4)'
        : 'none',
  }
}));

// Create a wrapper for NoteButton that properly handles boolean props and drag
const EnhancedNoteButton = ({ active, playing, isroot, inmode, isnext, coachblink, children, note, rootNote, mode, ...props }) => {
  return (
    <NoteButton
      active={active}
      playing={playing}
      isroot={isroot}
      inmode={inmode}
      isnext={isnext}
      coachblink={coachblink}
      style={{ cursor: 'pointer' }}
      {...props}
    >
      {children}
    </NoteButton>
  );
};

// Voicing toggle (1 drone / 3 triad / 7 seventh) accent per state. '3' is the
// neutral baseline (outlined), '1' and '7' each get a distinct accent.
const EXT_STYLES = {
  '1': { bg: 'rgba(8, 30, 34, 1)', fg: 'oklch(72% 0.12 200)', ring: 'oklch(58% 0.12 200)', glow: 'oklch(58% 0.12 200 / 0.4)' },
  '7': { bg: 'rgba(30, 20, 48, 1)', fg: 'oklch(70% 0.22 290)', ring: 'oklch(55% 0.22 290)', glow: 'oklch(55% 0.22 290 / 0.45)' }
};
const EXT_LABELS = { '1': 'drone', '3': 'triad', '7': '7th' };

// Libelle au survol : a la souris seulement, jamais sur tactile ou le survol
// n'existe pas et ou un tooltip resterait colle apres le tap.
const HoverHint = ({ title, enabled, children }) => (
  enabled
    ? <Tooltip title={title} enterDelay={400} placement="top" arrow>{children}</Tooltip>
    : children
);

// Convert to forwardRef to allow parent components to access methods
const NoteGrid = forwardRef(({
  rootNote,
  currentMode,
  activeChordNote,
  playingNote,
  onAudioInit,
  onPlayNote,
  onStopAllSounds,
  currentProgression,
  onProgressionChange,
  isProgressionPlaying: externalIsProgressionPlaying,
  onPlayingStateChange,
  onChordIndexChange,
  onProgressionPanelToggle,
  onRootNoteChange,
  onModeChange,
  // Metronome props
  metronomeEnabled,
  metronomeBPM,
  metronomeTimeSignature,
  metronomeVolume,
  metronomeVisualOnly,
  onMetronomeToggle,
  onMetronomeBPMChange,
  onMetronomeTimeSignatureChange,
  onMetronomeVolumeChange,
  onMetronomeVisualOnlyChange,
  onTapTempo,
  onStartMetronome,
  barsPerChord,
  onBarsPerChordChange,
  // Recording props
  isRecording = false,
  // Props for currentBeat and isDownbeat are now provided by SynthPlayer
  // Paint mode props
  isPaintMode = false,
  // Landscape mode
  isCompactMode = false,
  // Chord extension toggle: '3' (triads) | '7' (7th chords)
  chordExtension = '7',
  onChordExtensionToggle = () => {},
  onChordExtensionChange = () => {},
  // Bass mode: 'none' | 'static' | 'follow' (set from the chord-config popover)
  bassMode = 'static',
  onBassModeChange = () => {},
  // Coup de pouce au premier lancement : NoteGrid ne fait que rapporter les
  // gestes, l'automate (useGettingStarted) decide de ce qui compte.
  coachStep = null,
  onCoachChordTapped = () => {},
  onCoachPlayTapped = () => {},
  onCoachTipDismiss = () => {},
  isDesktop = false
}, ref) => {
  // Une ligne unique d'accords n'a de sens qu'en paysage : la largeur seule
  // faisait basculer la tablette en portrait (720 dp) sur le layout paysage.
  const singleRow = useMediaQuery('(min-width:700px) and (orientation: landscape)');
  const showHoverHints = isDesktop && canHover;
  const isShortScreen = useMediaQuery('(max-height:500px)');
  const [notesInMode, setNotesInMode] = useState([]);
  // Le bouton Play suit les chips dans la grille a trois colonnes : sa colonne
  // vaut donc le reste de leur nombre. Sur une pentatonique (5 notes) il tombe
  // au bord droit, ou un tip centre sortirait de l'ecran. En ligne unique le
  // metronome et la triade le suivent, il garde de la place des deux cotes.
  const [chordNames, setChordNames] = useState([]);
  const [chordQualities, setChordQualities] = useState([]);
  const [allChordNotes, setAllChordNotes] = useState([]); // Store chord notes for each scale degree
  const [scaleDegrees, setScaleDegrees] = useState([]);
  const [currentProgressionChordNote, setCurrentProgressionChordNote] = useState(null);
  // Barre reduite : pendant la lecture d'une progression choisie, les pastilles
  // suivent la grille jouee (ordre et repetitions comprises) au lieu des sept
  // degres. Un pas garde son index de degre tant qu'il reste dans la gamme
  // courante, et porte ses propres champs d'affichage si la grille module.
  const [progressionSteps, setProgressionSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(null);
  const previousPlaybackStateRef = useRef(null);
  const [localPlayingNote, setLocalPlayingNote] = useState(null);
  const [metronomePopoverAnchorEl, setMetronomePopoverAnchorEl] = useState(null);
  const [chordConfigAnchorEl, setChordConfigAnchorEl] = useState(null);

  // Toast state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastText, setToastText] = useState('');

  const containerRef = useRef(null);

  // Long press handler for metronome settings
  const longPressTimerRef = useRef(null);
  const metronomeButtonRef = useRef(null);

  // Long press handler for play button (progression selection)
  const playLongPressTimerRef = useRef(null);
  const playButtonRef = useRef(null);

  // Long press handler for the chord-extension button (opens the chord-config
  // popover: 1/3/7 voicing + bass mode). A short tap still cycles the voicing;
  // the guard ref suppresses that cycle once a long press has fired.
  const chordExtTimerRef = useRef(null);
  const chordExtButtonRef = useRef(null);
  const chordExtLongPressedRef = useRef(false);

  const openChordConfig = useCallback(() => {
    if (chordExtButtonRef.current) setChordConfigAnchorEl(chordExtButtonRef.current);
  }, []);

  const handleChordExtTouchStart = useCallback(() => {
    chordExtLongPressedRef.current = false;
    chordExtTimerRef.current = setTimeout(() => {
      chordExtLongPressedRef.current = true;
      openChordConfig();
    }, 500);
  }, [openChordConfig]);

  const handleChordExtTouchEnd = useCallback(() => {
    if (chordExtTimerRef.current) {
      clearTimeout(chordExtTimerRef.current);
      chordExtTimerRef.current = null;
    }
  }, []);

  const handleChordExtClick = useCallback(() => {
    // Suppress the cycle if this click is the tail of a long press.
    if (chordExtLongPressedRef.current) {
      chordExtLongPressedRef.current = false;
      return;
    }
    onChordExtensionToggle();
  }, [onChordExtensionToggle]);

  const handleMetronomeTouchStart = useCallback((e) => {
    longPressTimerRef.current = setTimeout(() => {
      // Long press detected - open settings popup
      if (metronomeButtonRef.current) {
        setMetronomePopoverAnchorEl(metronomeButtonRef.current);
      }
    }, 500); // 500ms long press threshold
  }, []);

  const handleMetronomeTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleMetronomeClick = useCallback(() => {
    // Only toggle if it wasn't a long press (popup not open)
    if (!metronomePopoverAnchorEl) {
      onMetronomeToggle();
    }
  }, [metronomePopoverAnchorEl, onMetronomeToggle]);

  // Long press handlers for play button (opens progression panel)
  const playLongPressTriggeredRef = useRef(false);

  const handlePlayTouchStart = useCallback((e) => {
    playLongPressTriggeredRef.current = false;
    playLongPressTimerRef.current = setTimeout(() => {
      // Long press detected - open progression panel
      playLongPressTriggeredRef.current = true;
      if (onProgressionPanelToggle) {
        onProgressionPanelToggle();
      }
    }, 500); // 500ms long press threshold
  }, [onProgressionPanelToggle]);

  const handlePlayTouchEnd = useCallback(() => {
    if (playLongPressTimerRef.current) {
      clearTimeout(playLongPressTimerRef.current);
      playLongPressTimerRef.current = null;
    }
  }, []);

  // Build the Roman numeral string for a progression from the actual chord
  // qualities of the current scale, so it always matches the chord pads
  // (e.g. Aeolian shows i-VII-VI-VII, not the hardcoded label baked into the name).
  // Falls back to the parenthetical in the name when scale degrees aren't ready.
  const getProgressionNumerals = (progressionName) => {
    if (!progressionName) return null;
    if (progressionName === RANDOM_PROGRESSION) return 'AUTO';
    const progression = getProgressionByName(progressionName);
    if (progression && Array.isArray(progression.degrees) && scaleDegrees.length > 0) {
      const numerals = progression.degrees.map(degree => {
        const index = degreeToIndex(degree, scaleDegrees.length);
        return scaleDegrees[index];
      });
      if (numerals.every(Boolean)) {
        return numerals.join('-');
      }
    }
    const match = progressionName.match(/\(([^)]+)\)/);
    return match ? match[1] : progressionName;
  };


  const getChordQualityForIndex = (index) => {
    if (typeof index !== 'number' || index < 0) {
      return null;
    }
    const quality = chordQualities[index];
    if (quality && typeof quality === 'string' && quality.trim() !== '') {
      return quality;
    }
    const chordName = chordNames[index];
    if (!chordName || typeof chordName !== 'string') {
      return null;
    }
    const rawQuality = chordName.split(' ').slice(1).join(' ').trim();
    if (!rawQuality) {
      return null;
    }
    const sanitized = rawQuality.replace(/[()]/g, '').trim();
    return sanitized !== '' ? sanitized : null;
  };

  // Resolve a progression's scale degrees into chord objects in the current
  // scale. Register is handled downstream by voiceChord (fixed window), so the
  // chord objects only need note + quality + scale context.
  const resolveProgressionChords = (degrees) => {
    return degrees.map(degree => {
      const index = degreeToIndex(degree, notesInMode.length);
      const note = notesInMode[index];
      return {
        note,
        quality: getChordQualityForIndex(index) || 'maj',
        rootNote,
        mode: currentMode,
        scaleRoot: rootNote
      };
    });
  };

  // Build the pool of short cadences the infinite random mode draws from. Uses
  // the same per-mode curated progressions the UI offers (so they sound musical
  // in this mode), each resolved into the current key. Re-runs on scale/voicing
  // changes so the pool always reflects the current scale.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const buildRandomProgressionPool = useCallback(() => {
    if (!notesInMode.length) return [];
    const names = getCompatibleProgressions(currentMode);
    const presets = names.map(getProgressionByName).filter(Boolean);
    // Always include the mode's idiomatic default, and fall back to a plain
    // I-IV-V-I if the mode lists nothing compatible.
    const fallback = getDefaultProgression(currentMode);
    if (fallback) presets.push(fallback);
    if (presets.length === 0) presets.push({ name: 'I-IV-V-I', degrees: [1, 4, 5, 1] });

    const seen = new Set();
    const pool = [];
    presets.forEach(p => {
      if (!p || !Array.isArray(p.degrees) || seen.has(p.name)) return;
      seen.add(p.name);
      const chords = resolveProgressionChords(p.degrees);
      if (chords.length > 0) pool.push({ name: p.name, chords });
    });
    return pool;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesInMode, rootNote, currentMode, chordExtension]);

  // Use the unified SynthPlayer hook.
  // subscribeBeat:false - nothing in NoteGrid renders the beat; subscribing would
  // re-render this whole component on every tick during playback.
  const synthPlayer = useSynthPlayer({ subscribeBeat: false });
  const { isPlaying: isProgressionPlaying, playbackState, state, setProgression, setRandomMode, setBPM, setTimeSignature, setBarsPerChord, setMetronomeEnabled, setMetronomeVisualOnly, setMetronomeVolume, tap, toggle, stop } = synthPlayer;

  // La barre reduite n'a de sens que sur une progression choisie : le mode
  // aleatoire tire sa sequence dans le player a chaque boucle sans la remonter
  // ici, la barre afficherait une grille perimee.
  //
  // Le bouton ne fait que lecture/pause, il n'envoie jamais le player sur
  // 'stopped' : la pause est l'etat de repos de fait. La barre reduite y reste
  // donc, sinon elle sauterait a chaque pause, mais un accord joue a la main
  // rend les sept degres. Sans cette porte de sortie, les degres absents de la
  // grille deviendraient injoignables.
  const showProgressionPads =
    (playbackState === 'playing' || (playbackState === 'paused' && !localPlayingNote)) &&
    currentProgression !== RANDOM_PROGRESSION &&
    progressionSteps.length > 0;

  const displayQualityOf = (chordName) =>
    chordName ? chordName.split(' ').slice(1).join(' ').trim() : '';

  // Une seule liste de pastilles pour les deux etats, la grille de rendu ne
  // connait plus que ce tableau.
  const pads = showProgressionPads
    ? progressionSteps.map((step, index) => {
        const inScale = step.degreeIndex >= 0;
        const degreeIndex = step.degreeIndex;
        const scaleNote = inScale ? notesInMode[degreeIndex] : null;
        return {
          key: `step-${index}`,
          note: inScale ? scaleNote : step.note,
          romanNumeral: inScale ? scaleDegrees[degreeIndex] : step.romanNumeral,
          quality: inScale ? (getChordQualityForIndex(degreeIndex) || 'maj') : step.quality,
          displayQuality: inScale ? displayQualityOf(chordNames[degreeIndex]) : step.displayQuality,
          chordNotes: inScale ? (allChordNotes[degreeIndex] || [scaleNote]) : step.chordNotes,
          isPlaying: index === currentStepIndex
        };
      })
    : notesInMode.map((note, index) => {
        // Compare by pitch class, not spelling: the pad may be spelled with a
        // flat (Eb) while rootNote/activeChordNote use the sharp key (D#).
        // During progression playback, highlight the current chord in the sequence.
        // Compare chromatic pitch, not the spelling: the progression's chord note can
        // be spelled differently from this pad (enharmonics, double sharps in keys like
        // D# Aeolian), which made the wrong pad - or none - light up.
        const padPitch = resolveAllNotesIndex(note);
        return {
          key: note,
          note,
          romanNumeral: scaleDegrees[index],
          quality: getChordQualityForIndex(index) || 'maj',
          displayQuality: displayQualityOf(chordNames[index]),
          chordNotes: allChordNotes[index] || [note],
          isPlaying: isProgressionPlaying
            ? padPitch !== -1 && padPitch === resolveAllNotesIndex(currentProgressionChordNote)
            : padPitch !== -1 && padPitch === resolveAllNotesIndex(playingNote)
        };
      });

  // Le bouton Play suit les pastilles dans la grille a trois colonnes : sa
  // colonne vaut donc le reste de leur nombre. Sur une pentatonique (5 notes)
  // il tombe au bord droit, ou un tip centre sortirait de l'ecran. En ligne
  // unique le metronome et la triade le suivent, il garde de la place des deux
  // cotes.
  const playColumn = pads.length % 3;
  const playTipAlign = isDesktop || singleRow
    ? 'center'
    : (playColumn === 0 ? 'start' : (playColumn === 1 ? 'center' : 'end'));

  // Log when props change
  useEffect(() => {
  }, [rootNote, currentMode, activeChordNote]);

  // Set up metronome settings
  useEffect(() => {
    setMetronomeEnabled(metronomeEnabled);
    setMetronomeVisualOnly(metronomeVisualOnly);
    setMetronomeVolume(metronomeVolume);
  }, [metronomeEnabled, metronomeVisualOnly, metronomeVolume, setMetronomeEnabled, setMetronomeVisualOnly, setMetronomeVolume]);

  // Set up timing settings
  useEffect(() => {
    setBPM(metronomeBPM);
    setTimeSignature(metronomeTimeSignature);
    setBarsPerChord(barsPerChord);
  }, [metronomeBPM, metronomeTimeSignature, barsPerChord, setBPM, setTimeSignature, setBarsPerChord]);

  // Notify parent of playing state changes - careful to avoid feedback loops
  useEffect(() => {
    if (onPlayingStateChange) {
      onPlayingStateChange(isProgressionPlaying, playbackState);
    }
    // Clear chord highlighting when progression stops
    if (playbackState === 'stopped') {
      setCurrentProgressionChordNote(null);
      setCurrentStepIndex(null);
    }
    // La progression reprend la main sur l'accord joue a la main, et la barre
    // reduite avec elle. Au seul passage en lecture : onPlayingStateChange
    // arrive en fonction anonyme, l'effet rejoue a chaque rendu du parent et
    // effacerait sinon l'accord tape pendant la lecture, dont le second tap
    // arrete la progression.
    if (playbackState === 'playing' && previousPlaybackStateRef.current !== 'playing') {
      setLocalPlayingNote(null);
    }
    previousPlaybackStateRef.current = playbackState;
  }, [isProgressionPlaying, playbackState, onPlayingStateChange]);
  
  // Get the notes and chord names for the current mode
  useEffect(() => {
    // Pentatonics get their own voicings (triads stacked within the 5-note scale,
    // per Per Nilsson), so one pad per pentatonic note. Heptatonic scales are
    // unaffected (getChordDescriptorsInMode handles each in its own branch).
    const notes = getNotesInMode(rootNote, currentMode);
    const chordDescriptors = getChordDescriptorsInMode(rootNote, currentMode, chordExtension);

    // Debug log for C Dorian #4 verification
    if (rootNote === 'C' && currentMode === 'Dorian #4') {
      
      chordDescriptors.forEach((chord, i) => {
        const expected = [
          'C–Eb–G (min)', 'D–F#–A (maj)', 'Eb–G–Bb (dim)',
          'F#–A–C (maj)', 'G–Bb–D (min)', 'A–C–Eb (dim)', 'Bb–D–F# (maj)'
        ][i];
        const actual = `${chord.notes.join('–')} (${chord.type})`;
        const match = actual === expected ? '✅' : '❌';
        
      });
    }

    const chords = chordDescriptors.map(chord => chord.displayName);
    const qualities = chordDescriptors.map(chord => chord.type);
    const chordNotesArray = chordDescriptors.map(chord => chord.notes); // Store chord notes from descriptors
    // Numerals follow the honest display naming; `qualities` (legacy keys) keeps
    // feeding the audio path untouched.
    const degrees = getScaleDegrees(notes.length, chordDescriptors.map(chord => chord.displayType || chord.type));

    // Reorder the notes to ensure the root note is first
    const rootIndex = notes.indexOf(rootNote);

    if (rootIndex > 0) {
      const reorderedNotes = [
        ...notes.slice(rootIndex),
        ...notes.slice(0, rootIndex)
      ];

      const reorderedChords = [
        ...chords.slice(rootIndex),
        ...chords.slice(0, rootIndex)
      ];

      const reorderedChordNotes = [
        ...chordNotesArray.slice(rootIndex),
        ...chordNotesArray.slice(0, rootIndex)
      ];

      const reorderedQualities = [
        ...qualities.slice(rootIndex),
        ...qualities.slice(0, rootIndex)
      ];

      const reorderedDegrees = [
        ...degrees.slice(rootIndex),
        ...degrees.slice(0, rootIndex)
      ];

      setNotesInMode(reorderedNotes);
      setChordNames(reorderedChords);
      setChordQualities(reorderedQualities);
      setAllChordNotes(reorderedChordNotes);
      setScaleDegrees(reorderedDegrees);
    } else {
      setNotesInMode(notes);
      setChordNames(chords);
      setChordQualities(qualities);
      setAllChordNotes(chordNotesArray);
      setScaleDegrees(degrees);
    }
  }, [rootNote, currentMode, chordExtension]);
  
  // Handle note click to play chord 
  const handleNoteClick = async (note) => {
    if (notesInMode.includes(note)) {
      try {
        const chordIndex = notesInMode.indexOf(note);
        let chordQuality = getChordQualityForIndex(chordIndex);
        if (!chordQuality) {
          chordQuality = 'maj';
        }

        // In paint mode, also play chord audio normally
        if (isPaintMode) {
          // If clicking the currently active note, stop it (compare by pitch class)
          if (resolveAllNotesIndex(activeChordNote) === resolveAllNotesIndex(note)) {
            if (onStopAllSounds) {
              onStopAllSounds();
            }
            synthPlayer.synthPlayer.stopChords();
            setLocalPlayingNote(null);
            return;
          }

          // Stop current sound if any
          if (localPlayingNote) {
            if (onStopAllSounds) {
              onStopAllSounds();
            }
            synthPlayer.synthPlayer.stopChords();
            setLocalPlayingNote(null);
          }

          // Play chord audio
          if (onPlayNote) {
            onPlayNote(note);
          }
          setLocalPlayingNote(note);
          synthPlayer.synthPlayer.switchChord({ note, quality: chordQuality, scaleRoot: rootNote }, null);
          return;
        }
        
        // Normal play mode - handle audio and visual updates
        // Check if there's a locally tracked playing note
        if (localPlayingNote) {
          
          // IMMEDIATE VISUAL UPDATE: Clear visual state first
          if (onStopAllSounds) {
            onStopAllSounds();
          }
          
          // Then stop audio using SynthPlayer
          synthPlayer.synthPlayer.stopChords();
          
          // Clear local state
          setLocalPlayingNote(null);
          
          // If clicking the same note that was playing, just stop (don't restart)
          if (localPlayingNote === note) {
            return;
          }
        }
        
        // IMMEDIATE VISUAL UPDATE: Set visual state before audio synthesis
        if (onPlayNote) {
          onPlayNote(note);
        }
        
        // Call the parent's audio initialization function if provided (non-blocking for visual updates)
        if (onAudioInit) {
          onAudioInit(); // Remove await to prevent blocking visual updates
        }
        
        // Then start audio synthesis using SynthPlayer
        synthPlayer.synthPlayer.stopChords(); // Stop any currently playing chords
        await synthPlayer.synthPlayer.switchChord({ note, quality: chordQuality, scaleRoot: rootNote }, null);
        
        // Set local playing note state
        setLocalPlayingNote(note);

        // Le son est reellement parti : c'est le bon moment pour le tip suivant.
        onCoachChordTapped();

        track('chord_tapped', { note, quality: chordQuality, root_note: rootNote, mode: currentMode });
      } catch (error) {
        console.error("[NoteGrid] Error playing chord:", error);
      }
    }
  };

  // When the voicing (drone / triad / 7th) changes while a chord is sounding,
  // re-trigger that chord with the new voicing so the change is heard live.
  useEffect(() => {
    if (!localPlayingNote || isProgressionPlaying) return;
    const descriptors = getChordDescriptorsInMode(rootNote, currentMode, chordExtension);
    const desc = descriptors.find(d => resolveAllNotesIndex(d.note) === resolveAllNotesIndex(localPlayingNote));
    const quality = desc?.type || 'maj';
    synthPlayer.synthPlayer.switchChord({ note: localPlayingNote, quality, scaleRoot: rootNote }, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chordExtension]);

  // Set up chord progression when it changes
  useEffect(() => {
    if (notesInMode.length > 0 && chordNames.length > 0) {
      // Random mode: hand the player a pool to draw from instead of a sequence.
      // Re-runs on scale changes so the pool always reflects the current scale.
      if (currentProgression === RANDOM_PROGRESSION) {
        setRandomMode(true, buildRandomProgressionPool());
        setProgressionSteps([]);
        return;
      }

      // Get the progression chords
      let progressionChords = [];

      if (!currentProgression) {
        // Auto-select the default progression for the current mode
        const defaultProgression = getDefaultProgression(currentMode);
        if (defaultProgression) {
          // Update the parent state to select this progression
          if (onProgressionChange) {
            onProgressionChange(defaultProgression.name);
          }
          const progressionIndices = defaultProgression.degrees.map(degree => degreeToIndex(degree, notesInMode.length));
          progressionChords = progressionIndices.map(index => ({
            note: notesInMode[index],
            quality: getChordQualityForIndex(index) || 'maj',
            rootNote: rootNote,
            mode: currentMode
          }));
        } else {
          // Fallback to a simple I-IV-V-I progression
          const progressionIndices = [0, 3, 4, 0].map(index => index % notesInMode.length);
          progressionChords = progressionIndices.map(index => ({
            note: notesInMode[index],
            quality: getChordQualityForIndex(index) || 'maj',
            rootNote: rootNote,
            mode: currentMode
          }));
        }
      } else {
        // Use the defined progression
        const progression = getProgressionByName(currentProgression);
        if (progression) {
          // Check if progression has per-chord root/mode data (new format)
          if (progression.steps) {
            // New format: each chord can have its own root and mode
            progressionChords = progression.steps.map(step => {
              // Use stored root and mode to get the correct notes and qualities.
              // Read the legacy quality key straight from the descriptors: the
              // display name is no longer a safe proxy for the audio quality.
              const stepNotes = getNotesInMode(step.rootNote, step.mode);
              const stepDescriptors = getChordDescriptorsInMode(step.rootNote, step.mode);

              // Find the index in the step's scale
              const noteIndex = stepNotes.indexOf(step.note);

              return {
                note: step.note,
                quality: stepDescriptors[noteIndex]?.type || 'maj',
                rootNote: step.rootNote,
                mode: step.mode
              };
            });
          } else {
            // Old format: use current root and mode for all chords
            const progressionIndices = progression.degrees.map(degree => degreeToIndex(degree, notesInMode.length));
            progressionChords = progressionIndices.map(index => ({
              note: notesInMode[index],
              quality: getChordQualityForIndex(index) || 'maj',
              rootNote: rootNote,
              mode: currentMode
            }));
          }
        } else {
          // Fallback to a simple I-IV-V-I progression
          const progressionIndices = [0, 3, 4, 0].map(index => index % notesInMode.length);
          progressionChords = progressionIndices.map(index => ({
            note: notesInMode[index],
            quality: getChordQualityForIndex(index) || 'maj',
            rootNote: rootNote,
            mode: currentMode
          }));
        }
      }
      
      // Log each chord to verify root/mode is included
      progressionChords.forEach((chord, index) => {
        
      });
      setProgression(progressionChords);

      // Tableau d'affichage separe : celui du player sert de signature, on n'y
      // ajoute rien. Un accord de la gamme courante n'a besoin que de son index
      // de degre, les autres (progressions modulantes) portent leurs champs.
      setProgressionSteps(progressionChords.map(chord => {
        const sameScale = chord.rootNote === rootNote && chord.mode === currentMode;
        const degreeIndex = sameScale ? notesInMode.indexOf(chord.note) : -1;
        if (degreeIndex !== -1) {
          return { degreeIndex };
        }
        const stepNotes = getNotesInMode(chord.rootNote, chord.mode);
        const stepDescriptors = getChordDescriptorsInMode(chord.rootNote, chord.mode, chordExtension);
        const stepDegrees = getScaleDegrees(stepNotes.length, stepDescriptors.map(d => d.displayType || d.type));
        const stepIndex = stepNotes.indexOf(chord.note);
        const descriptor = stepIndex !== -1 ? stepDescriptors[stepIndex] : null;
        const displayName = descriptor ? descriptor.displayName : '';
        return {
          degreeIndex: -1,
          note: chord.note,
          romanNumeral: stepIndex !== -1 ? stepDegrees[stepIndex] : '',
          quality: (descriptor && descriptor.type) || chord.quality || 'maj',
          displayQuality: displayName ? displayName.split(' ').slice(1).join(' ').trim() : '',
          chordNotes: (descriptor && descriptor.notes) || [chord.note]
        };
      }));
    }
  }, [currentProgression, currentMode, rootNote, chordExtension, notesInMode, chordNames, chordQualities, onProgressionChange, setProgression, setRandomMode, buildRandomProgressionPool]);

  // Set up callbacks for chord changes
  useEffect(() => {
    const handleChordChange = (chord, time, currentIndex) => {
      const currentNote = chord.note || chord.rootNote;

      // Set the current progression chord for highlighting
      setCurrentProgressionChordNote(currentNote);

      // If this chord has a different root or mode, update the UI
      if (chord.rootNote && chord.mode) {
        if (chord.rootNote !== rootNote && onRootNoteChange) {
          onRootNoteChange(chord.rootNote);
        }
        if (chord.mode !== currentMode && onModeChange) {
          onModeChange(chord.mode);
        }
      }

      // Pastille active de la barre reduite : par index, pas par hauteur de
      // note, sinon une grille qui repete un accord (ii-I-V-I) en allumerait
      // deux a la fois.
      if (currentIndex !== undefined) {
        setCurrentStepIndex(currentIndex);
      }

      // Notify parent of the current chord index
      if (onChordIndexChange && currentIndex !== undefined) {
        onChordIndexChange(currentIndex);
      }

      if (onPlayNote) {
        onPlayNote(currentNote);
      }
    };

    synthPlayer.synthPlayer.onChordChange(handleChordChange);
    
    return () => {
      synthPlayer.synthPlayer.offChordChange(handleChordChange);
    };
  }, [onPlayNote, onChordIndexChange, state, synthPlayer.synthPlayer, rootNote, currentMode, onRootNoteChange, onModeChange]);

  // Simple play button click handler using SynthPlayer
  const handlePlayButtonClick = async () => {
    // Don't toggle if long press was triggered
    if (playLongPressTriggeredRef.current) {
      playLongPressTriggeredRef.current = false;
      return;
    }

    onCoachPlayTapped();

    try {
      // Call the parent's audio initialization function if provided (for web fallback)
      if (onAudioInit) {
        await onAudioInit();
      }

      const wasState = synthPlayer.synthPlayer.playbackState;
      if (isRecording && wasState === 'playing') {
        stop();
      } else {
        // Use SynthPlayer's unified toggle function
        toggle();
      }

      const event = wasState === 'playing'
        ? (isRecording ? 'progression_stopped' : 'progression_paused')
        : (wasState === 'paused' ? 'progression_resumed' : 'progression_started');
      track(event, {
        progression: currentProgression, root_note: rootNote, mode: currentMode
      });
    } catch (error) {
      console.error("[NoteGrid] Error toggling playback:", error);
    }
  };

  // Start infinite random-chord playback. Builds the pool from the current scale,
  // enables random mode, and starts the transport if it is not already running.
  const playRandom = async () => {
    try {
      if (onAudioInit) {
        await onAudioInit();
      }
      const pool = buildRandomProgressionPool();
      if (!pool.length) return;
      setRandomMode(true, pool);
      setToastText(canHover ? 'Playing random chords' : 'Playing random chords\nLong press ■ to stop');
      setToastVisible(true);
      if (!isProgressionPlaying) {
        toggle();
      }
      track('progression_started', { progression: 'random', root_note: rootNote, mode: currentMode });
    } catch (error) {
      console.error('[NoteGrid] Error starting random chords:', error);
    }
  };

  // Expose the handlePlayButtonClick method to parent components
  useImperativeHandle(ref, () => ({
    handlePlayButtonClick: () => handlePlayButtonClick(),
    playRandom: () => playRandom(),
    // Raccourcis 1 a 7 : meme chemin que le tap sur la tuile du degre, init
    // audio comprise. Les gammes de moins de sept degres ignorent le surplus.
    playDegree: (index) => {
      const note = notesInMode[index];
      if (note) handleNoteClick(note);
    }
  }));

  // Render the chord grid view (view 0)
  const renderChordGrid = () => (
    <>
      <Grid
        className="note-grid-buttons"
        container
        spacing={0.5}
        justifyContent="center"
        alignItems="stretch"
      >
        {pads.map((pad, index) => {
          const note = pad.note;
          // Compare by pitch class, not spelling: the pad may be spelled with a
          // flat (Eb) while rootNote/activeChordNote use the sharp key (D#).
          const padPitch = resolveAllNotesIndex(note);
          const isRoot = padPitch !== -1 && padPitch === resolveAllNotesIndex(rootNote);
          const isPlaying = pad.isPlaying;

          const chordQuality = pad.quality;
          const displayQuality = pad.displayQuality;

          // Use the note directly as it's already properly spelled from getNotesInMode
          const displayNote = note;

          // The chord notes are already properly spelled from buildTriadFromScale
          const displayChordNotes = pad.chordNotes;

          const romanNumeral = pad.romanNumeral;
          // Diminished (and half-diminished) degrees are the unstable ones: flag
          // them in amber. Detect from the degree symbol or the chord quality.
          const isDim = (romanNumeral || '').includes('°') || (romanNumeral || '').includes('ø') || /dim|half_dim/.test(chordQuality);
          const dimColor = 'rgba(255, 255, 255, 0.55)';

          // Sur un chip de bord, un tip centre sortirait de l'ecran : on l'aligne
          // du cote ou il y a de la place. Le desktop garde le centrage, il a la
          // marge pour lui et le texte n'y est pas agrandi.
          const columnCount = singleRow ? pads.length + 3 : 3;
          const column = singleRow ? index : index % 3;
          const tipAlign = isDesktop
            ? 'center'
            : (column === 0 ? 'start' : (column === columnCount - 1 ? 'end' : 'center'));

          return (
            <Grid
              item
              key={pad.key}
              xs={singleRow ? true : 4}
              onPointerDown={() => handleNoteClick(note)}
              sx={{ cursor: 'pointer', position: 'relative' }}
            >
              {/* Hors du Paper du chip : celui-ci est a 0,9 d'opacite et rejoue
                  son fondu d'entree a chaque changement d'etat, le tip doit
                  rester net. */}
              {coachStep === 'tapTonic' && isRoot && !showProgressionPads && (
                <Tip text="Start here" align={tipAlign} />
              )}
              <EnhancedNoteButton
                playing={isPlaying}
                isroot={isRoot}
                inmode={true}
                coachblink={coachStep === 'tapTonic' && isRoot}
                note={note}
                rootNote={rootNote}
                mode={currentMode}
              >
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  position: 'relative',
                  pt: 0.25
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: isPlaying ? 1 : 0.8,
                      fontWeight: 500,
                      fontSize: singleRow ? '0.8rem' : '1.2rem',
                      color: isDim && !isPlaying ? dimColor : 'inherit',
                      position: 'absolute',
                      top: 2,
                      left: 5,
                      textAlign: 'left'
                    }}
                  >
                    {romanNumeral}
                  </Typography>
                  {isPlaying && (
                    <StopIcon
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 5,
                        fontSize: singleRow ? '0.8rem' : '1.2rem',
                        opacity: 0.9
                      }}
                    />
                  )}
                  <Typography
                    variant="h5"
                    component="div"
                    sx={{
                      fontWeight: 700,
                      fontSize: singleRow ? '2rem' : '2.5rem',
                      mt: -0.12
                    }}
                  >
                    <AccidentalText text={formatAccidentals(displayNote)} />
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      opacity: 0.7,
                      fontSize: singleRow ? '1rem' : '1.4rem',
                      lineHeight: 1,
                      minHeight: singleRow ? '1rem' : '1.4rem',
                      color: isDim && !isPlaying ? dimColor : 'inherit',
                      mt: -0.5
                    }}
                  >
                    <AccidentalText text={formatAccidentals(formatChordQuality(displayQuality || chordQuality))} />
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      opacity: 0.5,
                      fontSize: singleRow ? '0.8rem' : '0.85rem',
                      position: 'relative',
                      bottom: -2
                    }}
                  >
                    <AccidentalText text={formatAccidentals(displayChordNotes.join('—'))} />
                  </Typography>
                </Box>
              </EnhancedNoteButton>
            </Grid>
          );
        })}
        
        {/* Play Button (long press for progression selection) */}
        <Grid item xs={singleRow ? true : 2} sx={{}}>
          <Box
            ref={playButtonRef}
            onTouchStart={handlePlayTouchStart}
            onTouchEnd={handlePlayTouchEnd}
            onTouchCancel={handlePlayTouchEnd}
            onMouseDown={handlePlayTouchStart}
            onMouseUp={handlePlayTouchEnd}
            onMouseLeave={handlePlayTouchEnd}
            sx={{ height: '100%', position: 'relative' }}
          >
            <HoverHint title="Play the chords" enabled={showHoverHints}>
            <Box sx={{ height: '100%' }}>
            {/* Dans la boite du seul bouton Play : le tip ignore les taps de sa
                cible, il ne doit pas ignorer ceux du menu "..." a cote. */}
            {coachStep === 'tapPlay' && (
              <Tip text="Tap to play the chords" align={playTipAlign} duration={8000} onDismiss={onCoachTipDismiss} />
            )}
            <EnhancedNoteButton
              onClick={handlePlayButtonClick}
              active={false}
              playing={false}
              isroot={false}
              inmode={true}
              coachblink={coachStep === 'tapPlay'}
              sx={{
                backgroundColor: '#fff',
                color: '#000',
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#e9e9ec',
                },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
            <Box sx={CARD_CONTENT_SX}>
              {isProgressionPlaying ? (
                <PauseIcon sx={{
                  fontSize: '2.5rem',
                  color: 'inherit'
                }} />
              ) : (
                <PlayArrowIcon sx={{
                  fontSize: '2.5rem',
                  color: 'inherit'
                }} />
              )}
              <Typography
                variant="caption"
                component="div"
                sx={{
                  opacity: 0.85,
                  fontWeight: 600,
                  fontSize: { xs: '0.85rem', sm: '0.8rem' },
                  position: 'relative',
                  bottom: -2,
                  color: 'inherit'
                }}
              >
                {getProgressionNumerals(currentProgression)}
              </Typography>
            </Box>
          </EnhancedNoteButton>
            </Box>
            </HoverHint>
            <HoverHint title="Change the chord sequence" enabled={showHoverHints}>
              <CardOverflowButton
                label="Progression settings"
                tone="light"
                onOpen={() => { if (onProgressionPanelToggle) onProgressionPanelToggle(); }}
              />
            </HoverHint>
          </Box>
        </Grid>

        {/* Metronome Toggle Button (long press for settings) */}
        <Grid item xs={singleRow ? true : 2} sx={{}}>
          <Box
            ref={metronomeButtonRef}
            onTouchStart={handleMetronomeTouchStart}
            onTouchEnd={handleMetronomeTouchEnd}
            onTouchCancel={handleMetronomeTouchEnd}
            onMouseDown={handleMetronomeTouchStart}
            onMouseUp={handleMetronomeTouchEnd}
            onMouseLeave={handleMetronomeTouchEnd}
            sx={{ height: '100%', position: 'relative' }}
          >
            <EnhancedNoteButton
              onClick={handleMetronomeClick}
              active={metronomeEnabled}
              playing={metronomeEnabled}
              isroot={false}
              inmode={true}
              sx={{
                backgroundColor: metronomeEnabled ? 'rgba(var(--accent-rgb), 0.18)' : 'transparent',
                color: metronomeEnabled ? 'var(--accent)' : undefined,
                border: 'none',
                boxShadow: metronomeEnabled
                  ? 'inset 0 0 0 2px var(--accent), 0 0 10px rgba(var(--accent-rgb), 0.4) !important'
                  : 'inset 0 0 0 1px rgba(255, 255, 255, 0.12)',
                '&:hover': {
                  backgroundColor: metronomeEnabled ? 'rgba(var(--accent-rgb), 0.18)' : undefined,
                },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Box sx={CARD_CONTENT_SX}>
                <MetronomeIcon sx={{ fontSize: '2rem', color: 'inherit' }} />
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    opacity: 0.85,
                    fontWeight: 600,
                    fontSize: { xs: '0.85rem', sm: '0.8rem' },
                    position: 'relative',
                    bottom: -2,
                    color: 'inherit'
                  }}
                >
                  {metronomeTimeSignature}
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    opacity: 0.7,
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', sm: '0.6rem' },
                    lineHeight: 1,
                    color: 'inherit'
                  }}
                >
                  {metronomeBPM} BPM
                </Typography>
              </Box>
            </EnhancedNoteButton>
            <CardOverflowButton
              label="Metronome settings"
              onOpen={() => setMetronomePopoverAnchorEl(metronomeButtonRef.current)}
            />
          </Box>
        </Grid>

        {/* Chord Extension Toggle (1 / 3 / 7) + chord-config popover */}
        <Grid item xs={singleRow ? true : 2} sx={{}}>
          <Box
            ref={chordExtButtonRef}
            onTouchStart={handleChordExtTouchStart}
            onTouchEnd={handleChordExtTouchEnd}
            onTouchCancel={handleChordExtTouchEnd}
            onMouseDown={handleChordExtTouchStart}
            onMouseUp={handleChordExtTouchEnd}
            onMouseLeave={handleChordExtTouchEnd}
            sx={{ height: '100%', position: 'relative' }}
          >
            <EnhancedNoteButton
              onClick={handleChordExtClick}
              active={chordExtension !== '3'}
              playing={chordExtension !== '3'}
              isroot={false}
              inmode={true}
              sx={{
                backgroundColor: EXT_STYLES[chordExtension]?.bg || 'transparent',
                color: EXT_STYLES[chordExtension]?.fg,
                border: 'none',
                boxShadow: EXT_STYLES[chordExtension]
                  ? `inset 0 0 0 2px ${EXT_STYLES[chordExtension].ring}, 0 0 10px ${EXT_STYLES[chordExtension].glow} !important`
                  : 'inset 0 0 0 1px rgba(255, 255, 255, 0.12)',
                '&:hover': {
                  backgroundColor: EXT_STYLES[chordExtension]?.bg
                },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              aria-label="Cycle chord voicing: drone, triad, 7th"
            >
              <Box sx={CARD_CONTENT_SX}>
                <Typography
                  component="div"
                  sx={{
                    fontWeight: 800,
                    fontSize: '2rem',
                    lineHeight: 1,
                    color: 'inherit',
                    transition: 'opacity 180ms ease, transform 180ms ease',
                    opacity: 1
                  }}
                >
                  {chordExtension}
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    opacity: 0.85,
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.7rem' },
                    position: 'relative',
                    bottom: -2,
                    color: 'inherit',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}
                >
                  {EXT_LABELS[chordExtension]}
                </Typography>
              </Box>
            </EnhancedNoteButton>
            <CardOverflowButton
              label="Chord and bass settings"
              onOpen={openChordConfig}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Box
      className="note-grid-container"
      ref={containerRef}
      sx={{
        width: 'calc(100% - 10px)',
        maxWidth: '936px',
        mx: 'auto',
        mb: 2,
        borderTop: 'none',
        boxSizing: 'border-box'
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {/* La consigne parle du manche, pas d'un chip : centree sur l'ecran, a la
            hauteur des bulles de chip, sans fleche. Le wrapper de taille
            nulle sert d'ancre, et fait qu'un tap sur un chip compte bien comme
            une interaction qui l'efface. */}
        {coachStep === 'droneTip' && (
          <Box sx={{ position: 'absolute', top: 0, left: '50%', width: 0, height: 0 }}>
            <Tip
              text="Grab your guitar. Play anything, land on a square."
              arrow="none"
              duration={8000}
              onDismiss={onCoachTipDismiss}
            />
          </Box>
        )}
        {renderChordGrid()}
      </Box>

      {/* Metronome Settings Popover */}
      <Popover
        open={Boolean(metronomePopoverAnchorEl)}
        anchorEl={metronomePopoverAnchorEl}
        onClose={() => setMetronomePopoverAnchorEl(null)}
        // Sur un ecran bas (telephone en paysage) l'ancrage au bouton pousse le
        // panneau hors champ : on le centre a l'ecran a la place.
        anchorReference={isShortScreen ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
        marginThreshold={8}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={isShortScreen ? {
          vertical: 'center',
          horizontal: 'center',
        } : {
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{ sx: { bgcolor: '#15151A', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' } }}
      >
        <MetronomeSettings
          metronomeEnabled={metronomeEnabled}
          metronomeBPM={metronomeBPM}
          metronomeTimeSignature={metronomeTimeSignature}
          metronomeVolume={metronomeVolume}
          metronomeVisualOnly={metronomeVisualOnly}
          onMetronomeToggle={onMetronomeToggle}
          onMetronomeBPMChange={onMetronomeBPMChange}
          onMetronomeTimeSignatureChange={onMetronomeTimeSignatureChange}
          onMetronomeVolumeChange={onMetronomeVolumeChange}
          onMetronomeVisualOnlyChange={onMetronomeVisualOnlyChange}
          onTapTempo={onTapTempo}
          barsPerChord={barsPerChord}
          onBarsPerChordChange={onBarsPerChordChange}
        />
      </Popover>

      {/* Chord config popover (1/3/7 voicing + bass mode) */}
      <Popover
        open={Boolean(chordConfigAnchorEl)}
        anchorEl={chordConfigAnchorEl}
        onClose={() => setChordConfigAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ sx: { bgcolor: '#15151A', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' } }}
      >
        <ChordConfigPanel
          chordExtension={chordExtension}
          onChordExtensionChange={onChordExtensionChange}
          bassMode={bassMode}
          onBassModeChange={onBassModeChange}
        />
      </Popover>

      {/* Custom Toast for feedback messages */}
      <CustomToast
        text={toastText}
        duration={3}
        visible={toastVisible}
        onHide={() => setToastVisible(false)}
      />
    </Box>
  );
});

export default NoteGrid;
